import { ArrowRightAltRounded, ContactPhone } from "@mui/icons-material"
import {
  Avatar,
  Box,
  Button,
  Dialog,
  Fade,
  List,
  ListItemAvatar,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material"
import { CloseIconButton } from "components/Button"
import { Head } from "components/Head"
import { CloseIcon } from "components/icons"
import { Header } from "extensions/iconsiam/components/Header"
import { HeaderBackButton } from "extensions/iconsiam/components/Header/HeaderBackButton"
import { PageLayout } from "extensions/iconsiam/layouts/PageLayout"
import { useFeatureDetail } from "hooks/feature"
import { compact, first, get, values } from "lodash"
import { useTranslation } from "providers/i18n"
import { useCallback, useMemo, useState } from "react"
import { IOccupantDetail } from "utils/feature"
import { getIcon } from "utils/icon"
import {
  getReservationAppDataByLink,
  getSocialPlatformDataByLink,
} from "utils/platform"

export const ReservationPage = ({ Layout = PageLayout }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`ReservationPage`] || {}
  const {
    root,
    icon,
    closeButton,
    listItemButton,
    listItemAvatar,
    text,
    dialogContent,
    dialog,
    dialogPrimaryText,
    buttonWrapper,
    phoneIcon: phoneIconStyle,
    phoneButton,
  } = styleOverrides

  const { reservations, phone, canReserve, logo } =
    useFeatureDetail() as IOccupantDetail

  const arrowRightIcon = useMemo(
    () =>
      getIcon(theme?.assets?.arrowRightIcon, icon) || <ArrowRightAltRounded />,
    [theme, icon]
  )
  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )
  const phoneIcon = useMemo(
    () => getIcon(theme?.assets?.phoneIcon, phoneIconStyle) || <ContactPhone />,
    [theme, phoneIconStyle]
  )
  const [open, setOpen] = useState(false)

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleClickPhone = useCallback((contact) => {
    window.open(contact)
  }, [])

  const handleClickUrl = useCallback((url) => {
    window.open(url, "_blank")
  }, [])

  const reservationMethods = useMemo(
    () =>
      reservations?.map((reservation) => {
        const { ios_link, android_link, link } = reservation || {}
        const links = values(reservation)
        const platform = first(
          compact(
            links.map(
              (link) =>
                getReservationAppDataByLink(`${link}`) ||
                getSocialPlatformDataByLink(`${link}`)
            )
          )
        )
        const platformLogo = get(
          theme,
          `assets.platforms[${platform?.reference}].url`
        )
        const platformName = platform?.platform
        return {
          logo: platformLogo || logo,
          name: platformName || t("Reservation"),
          link: {
            ios: ios_link,
            android: android_link,
            others: link,
          },
        }
      }),
    [theme, reservations, logo, t]
  )

  const userPlatform = useMemo(() => {
    const ua = navigator.userAgent

    const isIOS = /iPad|iPhone|iPod/.test(ua)
    const isAndroid = /android/i.test(ua)

    if (isIOS) return "ios"
    if (isAndroid) return "android"
    return "others"
  }, [])

  const isRtl = theme.isRtl

  if (!canReserve) return null

  return (
    <>
      <Head title={"Reservation"} />
      <Layout
        header={
          <Header
            headerTitle={t("Reservation")}
            backButton={<HeaderBackButton />}
          />
        }
      >
        <List sx={root}>
          {reservationMethods.map((method) => {
            const { logo, link, name } = method
            const platformLink = link[userPlatform] || link["others"]
            return (
              <ListItemButton
                onClick={() => handleClickUrl(platformLink)}
                sx={listItemButton}
                key={`reservation-${name}`}
              >
                <ListItemAvatar sx={listItemAvatar}>
                  <Avatar src={logo} />
                </ListItemAvatar>
                <Typography variant="subtitle2" sx={text}>
                  {name}
                </Typography>
                <Box className={isRtl ? "is-rtl" : ""}>{arrowRightIcon}</Box>
              </ListItemButton>
            )
          })}
          {phone && (
            <ListItemButton onClick={() => handleOpen()} sx={listItemButton}>
              <ListItemAvatar sx={listItemAvatar}>{phoneIcon}</ListItemAvatar>
              <Typography sx={text} variant="subtitle2">
                {t("Booking by phone")}
              </Typography>
              <Box className={isRtl ? "is-rtl" : ""}>{arrowRightIcon}</Box>
            </ListItemButton>
          )}
        </List>
      </Layout>
      <Dialog open={open} onClose={handleClose} sx={dialog}>
        <Fade in={open} timeout={500}>
          <Box sx={dialogContent}>
            <CloseIconButton
              sx={closeButton}
              size="small"
              onClick={handleClose}
              icon={closeIcon}
            />
            <Typography sx={dialogPrimaryText} variant="subtitle2">
              {t("Booking by phone")}
            </Typography>
            <Box sx={buttonWrapper}>
              <Button
                variant="text"
                onClick={() => handleClickPhone(`tel:${phone}`)}
                startIcon={phoneIcon}
                sx={phoneButton}
              >
                {phone}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Dialog>
    </>
  )
}
